"use client";

import { createInstance } from "i18next";
import type { ReactNode } from "react";
import { createContext, useMemo } from "react";
import { I18nextProvider, initReactI18next } from "react-i18next";

type AppRouterI18nContextType = {
  translations: Record<string, string>;
  ns: string;
  locale: string;
};

export const AppRouterI18nContext = createContext<AppRouterI18nContextType | null>(null);

export function AppRouterI18nProvider({
  children,
  translations,
  locale,
  ns,
}: AppRouterI18nContextType & {
  children: ReactNode;
}) {
  const i18n = useMemo(() => {
    const instance = createInstance();
    instance.use(initReactI18next).init({
      lng: locale,
      ns,
      defaultNS: ns,
      resources: {
        [locale]: {
          [ns]: translations,
        },
      },
      initImmediate: false,
    });
    return instance;
  }, [locale, ns, translations]);

  // Memoize the value to prevent re-renders unless the data changes
  const value = useMemo(
    () => ({
      translations,
      locale,
      ns,
    }),
    [locale, ns, translations]
  );

  return (
    <I18nextProvider i18n={i18n}>
      <AppRouterI18nContext.Provider value={value}>{children}</AppRouterI18nContext.Provider>
    </I18nextProvider>
  );
}
